/**
 * Store Assignment Service
 * 
 * Handles Firestore operations for store assignments.
 * Provides methods for assigning/removing stores and managing assignment history.
 */

import {
  doc,
  getDoc,
  updateDoc,
  arrayUnion,
  arrayRemove,
  Timestamp,
  getFirestore
} from 'firebase/firestore';
import { Store, StoreAssignment } from '../@types/store';

export class StoreAssignmentService {
  private db = getFirestore();

  /**
   * Assign a store to a user
   */
  async assignStoreToUser(
    storeId: string,
    userId: string,
    username: string,
    assignedBy: string,
    assignmentType: 'auto' | 'manual' = 'manual',
    notes?: string
  ): Promise<void> {
    try {
      const storeRef = doc(this.db, 'Stores', storeId);
      const userRef = doc(this.db, 'users', userId);

      // Create assignment record
      const assignment: StoreAssignment = {
        userId,
        username,
        assignedBy,
        assignedAt: Timestamp.now(),
        assignmentType,
        notes
      };

      // Check if already assigned to avoid duplicate assignments
      const storeSnap = await getDoc(storeRef);
      const storeData = storeSnap.data();
      const currentAssignedIds = storeData?.assignedToUserIds || [];
      
      if (currentAssignedIds.includes(userId)) {
        console.log(`ℹ️ Store ${storeId} already assigned to user ${userId}, skipping`);
        return; // Already assigned, no need to do anything
      }

      // Update store document
      await updateDoc(storeRef, {
        assignedToUserIds: arrayUnion(userId),
        assignedToUsernames: arrayUnion(username),
        assignmentHistory: arrayUnion(assignment),
        lastAssignedBy: assignedBy,
        lastAssignedAt: Timestamp.now()
      });

      // Update user document
      const userSnap = await getDoc(userRef);
      const userData = userSnap.data();
      const currentUserStoreIds = userData?.assignedStoreIds || [];
      
      if (!currentUserStoreIds.includes(storeId)) {
        await updateDoc(userRef, {
          assignedStoreIds: arrayUnion(storeId),
          lastStoreSync: Timestamp.now()
        });
      }

      console.log(`✅ Assigned store ${storeId} to user ${userId}`);
    } catch (error) {
      console.error('Error assigning store to user:', error);
      throw new Error(`Failed to assign store: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Remove store assignment from a user
   */
  async removeStoreAssignment(
    storeId: string,
    userId: string,
    removedBy: string,
    assignmentType: 'auto' | 'manual' = 'manual'
  ): Promise<void> {
    try {
      const storeRef = doc(this.db, 'Stores', storeId);
      const userRef = doc(this.db, 'users', userId);

      // Get current store data to find the username
      const storeSnap = await getDoc(storeRef);
      const storeData = storeSnap.data();
      
      // Get current user data to find the username
      const userSnap = await getDoc(userRef);
      const userData = userSnap.data();
      const username = userData?.username || userData?.email || '';

      // Find the assignment in history and mark as removed
      const history = storeData?.assignmentHistory || [];
      const updatedHistory = history.map((assignment: StoreAssignment) => {
        if (assignment.userId === userId && !assignment.removedAt) {
          return {
            ...assignment,
            removedAt: Timestamp.now(),
            removedBy
          };
        }
        return assignment;
      });

      // Update store document
      await updateDoc(storeRef, {
        assignedToUserIds: arrayRemove(userId),
        assignedToUsernames: arrayRemove(username),
        assignmentHistory: updatedHistory,
        lastAssignedBy: removedBy,
        lastAssignedAt: Timestamp.now()
      });

      // Update user document
      await updateDoc(userRef, {
        assignedStoreIds: arrayRemove(storeId),
        lastStoreSync: Timestamp.now()
      });

      console.log(`✅ Removed store ${storeId} from user ${userId}`);
    } catch (error) {
      console.error('Error removing store assignment:', error);
      throw new Error('Failed to remove store assignment');
    }
  }

  /**
   * Bulk assign multiple stores to a user
   */
  async bulkAssignStores(
    storeIds: string[],
    userId: string,
    username: string,
    assignedBy: string,
    assignmentType: 'auto' | 'manual' = 'manual'
  ): Promise<{ success: number; failed: number; errors: string[] }> {
    const results = {
      success: 0,
      failed: 0,
      errors: [] as string[]
    };

    for (const storeId of storeIds) {
      try {
        await this.assignStoreToUser(storeId, userId, username, assignedBy, assignmentType);
        results.success++;
      } catch (error) {
        results.failed++;
        results.errors.push(`Failed to assign store ${storeId}: ${error}`);
      }
    }

    return results;
  }

  /**
   * Bulk remove multiple store assignments from a user
   */
  async bulkRemoveStores(
    storeIds: string[],
    userId: string,
    removedBy: string,
    assignmentType: 'auto' | 'manual' = 'manual'
  ): Promise<{ success: number; failed: number; errors: string[] }> {
    const results = {
      success: 0,
      failed: 0,
      errors: [] as string[]
    };

    for (const storeId of storeIds) {
      try {
        await this.removeStoreAssignment(storeId, userId, removedBy, assignmentType);
        results.success++;
      } catch (error) {
        results.failed++;
        results.errors.push(`Failed to remove store ${storeId}: ${error}`);
      }
    }

    return results;
  }

  /**
   * Toggle auto-assignment for a user
   */
  async toggleAutoAssignment(
    userId: string,
    enabled: boolean
  ): Promise<void> {
    try {
      const userRef = doc(this.db, 'users', userId);
      
      await updateDoc(userRef, {
        autoAssignBySalesGroup: enabled,
        lastStoreSync: Timestamp.now()
      });

      console.log(`✅ Auto-assignment ${enabled ? 'enabled' : 'disabled'} for user ${userId}`);
    } catch (error) {
      console.error('Error toggling auto-assignment:', error);
      throw new Error('Failed to toggle auto-assignment');
    }
  }

  /**
   * Sync user's store assignments (useful after salesGroup changes)
   */
  async syncStoreAssignments(userId: string): Promise<void> {
    try {
      const userRef = doc(this.db, 'users', userId);
      
      await updateDoc(userRef, {
        lastStoreSync: Timestamp.now()
      });

      console.log(`✅ Synced store assignments for user ${userId}`);
    } catch (error) {
      console.error('Error syncing store assignments:', error);
      throw new Error('Failed to sync store assignments');
    }
  }

  /**
   * Get assignment history for a store
   */
  async getAssignmentHistory(storeId: string): Promise<StoreAssignment[]> {
    try {
      const storeRef = doc(this.db, 'Stores', storeId);
      const storeSnap = await getDoc(storeRef);
      
      if (!storeSnap.exists()) {
        throw new Error('Store not found');
      }

      const storeData = storeSnap.data();
      return storeData.assignmentHistory || [];
    } catch (error) {
      console.error('Error getting assignment history:', error);
      throw new Error('Failed to get assignment history');
    }
  }

  /**
   * Get all stores assigned to a user (from Firestore)
   */
  async getUserAssignedStoreIds(userId: string): Promise<string[]> {
    try {
      const userRef = doc(this.db, 'users', userId);
      const userSnap = await getDoc(userRef);
      
      if (!userSnap.exists()) {
        throw new Error('User not found');
      }

      const userData = userSnap.data();
      return userData.assignedStoreIds || [];
    } catch (error) {
      console.error('Error getting user assigned stores:', error);
      throw new Error('Failed to get user assigned stores');
    }
  }

  /**
   * Check if a store is assigned to a user (from Firestore)
   */
  async isStoreAssignedToUser(storeId: string, userId: string): Promise<boolean> {
    try {
      const storeRef = doc(this.db, 'Stores', storeId);
      const storeSnap = await getDoc(storeRef);
      
      if (!storeSnap.exists()) {
        return false;
      }

      const storeData = storeSnap.data();
      const assignedUserIds = storeData.assignedToUserIds || [];
      
      return assignedUserIds.includes(userId);
    } catch (error) {
      console.error('Error checking store assignment:', error);
      return false;
    }
  }
}
